import React from "react";
import { Block, Sub, N, Conv, Bar, th, thr, td, tdr } from "./shared";
import { TIER_BANDS, PRICE_VS, fmtEur, fmtPct } from "../labels";

function TierTable({ tiers, drill }) {
  const maxSales = Math.max(...tiers.map((t) => t.sales_eur || 0), 1);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]" data-testid="kk-budget-tiers-table">
        <thead><tr className="border-b border-clay-300">
          <th className={th}>Categoría</th><th className={thr}>Leads</th><th className={thr}>Vendidos</th><th className={thr}>No vendidos</th><th className={thr}>En curso</th>
          <th className={thr}>Conversión</th><th className={thr}>Venta</th><th className={thr}>Ingreso neto</th><th className={thr}>Margen</th><th className={thr}>Ticket medio</th><th className={thr}>Margen medio</th>
        </tr></thead>
        <tbody>
          {tiers.map((t) => (
            <tr key={t.tier} className="border-b border-clay-100" data-testid={`kk-tier-row-${t.tier}`}>
              <td className={`${td} font-medium`}>{TIER_BANDS[t.tier] || t.tier}</td>
              <td className={tdr}><N value={t.leads} onClick={() => drill({ tier: t.tier })}/></td>
              <td className={tdr}><N value={t.sold} onClick={() => drill({ tier: t.tier, bucket: "sold" })} tone="text-emerald-800"/>{t.sales_count > t.sold ? <span className="text-[10px] text-clay-500" title="ventas de leads anteriores a 2026 (solo cuentan en venta y margen)"> (+{t.sales_count - t.sold} de 2025)</span> : null}</td>
              <td className={tdr}><N value={t.not_sold} onClick={() => drill({ tier: t.tier, bucket: "not_sold" })}/></td>
              <td className={tdr}><N value={t.open} onClick={() => drill({ tier: t.tier, bucket: "open" })}/></td>
              <td className={tdr}><Conv value={t.conversion}/></td>
              <td className={tdr}><Bar value={t.sales_eur} max={maxSales}/> {fmtEur(t.sales_eur)}</td>
              <td className={tdr}>{fmtEur(t.net_eur)}</td>
              <td className={tdr}>{fmtEur(t.margin_eur)}{t.margin_pct != null && <span className="text-clay-500"> · {fmtPct(t.margin_pct)}</span>}</td>
              <td className={tdr}>{fmtEur(t.avg_ticket)}</td>
              <td className={tdr}>{fmtEur(t.avg_margin)}{t.no_breakdown ? <span className="text-[10px] text-clay-500" title="ventas sin desglose de precio leído"> ({t.no_breakdown} sin desglose)</span> : null}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PriceVsTable({ tiers, drill }) {
  const rows = tiers.filter((t) => Object.values(t.price_vs || {}).some((v) => v.leads));
  if (!rows.length) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]" data-testid="kk-price-vs-table">
        <thead><tr className="border-b border-clay-300">
          <th className={th}>Categoría</th>
          {["dentro", "encima", "debajo"].map((k) => <th key={k} className={thr}>{PRICE_VS[k]}<div className="font-normal text-[10px]">% propuestas · conversión</div></th>)}
        </tr></thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.tier} className="border-b border-clay-100">
              <td className={`${td} font-medium`}>{TIER_BANDS[t.tier] || t.tier}</td>
              {["dentro", "encima", "debajo"].map((k) => {
                const v = t.price_vs[k] || {};
                return (
                  <td key={k} className={tdr}>
                    {v.leads ? <>
                      <N value={v.leads} onClick={() => drill({ tier: t.tier, price_vs: k, bucket: "not_sold" })} testid={`kk-pricevs-${t.tier}-${k}`}/> <span className="text-clay-500">({fmtPct(v.share)})</span> → <Conv value={v.conversion}/>
                    </> : <span className="text-clay-400">—</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AgentTierTable({ agents, tiers, drill }) {
  const cols = tiers.map((t) => t.tier);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]" data-testid="kk-agent-tier-table">
        <thead><tr className="border-b border-clay-300">
          <th className={th}>Agente</th>
          {cols.map((c) => <th key={c} className={thr}>{TIER_BANDS[c] || c}<div className="font-normal text-[10px]">leads · conv · venta</div></th>)}
        </tr></thead>
        <tbody>
          {agents.map((a) => (
            <tr key={a.agent_email || "none"} className="border-b border-clay-100">
              <td className={`${td} font-medium`}>{a.agent_name}</td>
              {cols.map((c) => {
                const v = a.tiers[c];
                const ag = a.agent_email || "__none__";
                return (
                  <td key={c} className={tdr}>
                    {v ? <>
                      <N value={v.leads} onClick={() => drill({ agent: ag, tier: c })}/> · <Conv value={v.conversion}/>
                      {v.sold ? <div className="text-[10px] text-clay-600"><N value={v.sold} onClick={() => drill({ agent: ag, tier: c, bucket: "sold" })} tone="text-emerald-800"/> vend. · {fmtEur(v.sales_eur)} · m. {fmtEur(v.margin_eur)}</div> : null}
                    </> : <span className="text-clay-300">—</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function BudgetBlock({ data, drill }) {
  return (
    <Block id="budget" title="Rendimiento por presupuesto" subtitle="conversión sobre cerrados · venta = última propuesta enviada (EUR)">
      <Sub title="Por categoría de KimKim"><TierTable tiers={data.tiers} drill={drill}/></Sub>
      <Sub title="Precio propuesto frente al rango pedido" hint="último precio enviado por persona y día vs el rango de la solicitud · ¿vendemos más cuando nos ajustamos?"><PriceVsTable tiers={data.tiers} drill={drill}/></Sub>
      <Sub title="Agente × categoría" hint="quién convierte bien en qué nivel de presupuesto"><AgentTierTable agents={data.agents} tiers={data.tiers} drill={drill}/></Sub>
    </Block>
  );
}
